<template>
  <div>
    <div class="tw-flex tw-mb-12">
      <button
        @click="setTender('credit_card')" 
        :class="['tw-flex-1 tw-border tw-border-black tw-border-r-0 tw-text-center tw-px-8 tw-py-4 tw-uppercase tw-font-bold tw-rounded-l-full tw-transition-all', {'tw-bg-orange-medium tw-text-white': tender == 'credit_card'}, {'tw-text-black': tender != 'credit_card'}, {'tw-rounded-r-full': !supportsGiftCard && !supportsRefuel}]">Credit Card</button>
      <button
        v-if="supportsGiftCard"
        @click="setTender('gift_card')" 
        :class="['tw-flex-1 tw-border tw-border-black tw-text-center tw-px-8 tw-py-4 tw-uppercase tw-font-bold tw-transition-all', {'tw-bg-orange-medium tw-text-white': tender == 'gift_card'}, {'tw-rounded-r-full': (!supportsRefuel || props.deliverymode == 'delivery')}, {'tw-text-black': tender != 'gift_card'}]">Gift Card</button>
      <button
        v-if="supportsRefuel && props.deliverymode != 'delivery'"
        @click="setTender('refuel')" 
        :class="['tw-flex-1 tw-border tw-border-black tw-border-l-0 tw-text-center tw-px-8 tw-py-4 tw-uppercase tw-font-bold tw-rounded-r-full tw-transition-all', {'tw-bg-orange-medium tw-text-white': tender == 'refuel'}, {'tw-text-black': tender != 'refuel'}]">Refuel</button>
    </div>

    <template v-if="tender == 'credit_card'">
      <div class="lg:tw-flex tw-mb-12">

        <TwCheckbox
            id="cc_type_saved"
            key="cc_type_saved"
            v-if="user && savedCreditCards && savedCreditCards.length > 0"
            label="Saved Credit Card"
            value="saved"
            :formType="'radio'"
            class="tw-mb-4 tw-mr-12"
            :checked="cc_type == 'saved'"
            @on-change="setCCType('saved')"
          />

        <TwCheckbox
          id="cc_type_new"
          key="cc_type_new"
          label="New Credit Card"
          value="new"
          :formType="'radio'"
          class="tw-mb-4"
          :checked="cc_type == 'new'"
          @on-change="setCCType('new')"
        />
      </div> 

      <div class="tw-flex tw-flex-wrap" v-if="cc_type == 'saved'">
        <BillingAccountCard
          class="tw-overflow-hidden tw-mr-8 tw-mb-8 tw-rounded-2xl tw-block tw-group tw-cursor-pointer tw-w-128"
          v-for="card in savedCreditCards"
          :key="card.accountid"
          :selected="props.selectedPayment != null && props.selectedPayment.billingaccountid == card.accountid"
          :billingAccount="card"
          @setBillingAccount="billingAccountSet"
          />
      </div>

      <CreditCardForm 
        @setCreditCard="creditCardSet"
        :canSaveCard="user"
        v-if="cc_type == 'new'"
      />
    </template>

    <template v-if="tender == 'gift_card'">
      <div class="lg:tw-flex tw-mb-12">
        <TwCheckbox
          id="gc_type_saved"
          key="gc_type_saved"
          v-if="user && savedGiftCards && savedGiftCards.length > 0"
          label="Saved Gift Card"
          value="saved"
          :formType="'radio'"
          class="tw-mb-4 tw-mr-12"
          :checked="gc_type == 'saved'"
          @on-change="setGCType('saved')"
        />

        <TwCheckbox
          id="gc_type_new"
          key="gc_type_new"
          label="New Gift Card"
          value="new"
          :formType="'radio'"
          class="tw-mb-4"
          :checked="gc_type == 'new'"
          @on-change="setGCType('new')"
        />
      </div>

      <div class="tw-flex tw-flex-wrap" v-if="gc_type == 'saved'">
        <BillingAccountCard
          class="tw-shrink-0 tw-overflow-hidden tw-mr-8 tw-mb-8 tw-rounded-2xl tw-block tw-group tw-cursor-pointer tw-w-128"
          v-for="card in savedGiftCards"
          :key="card.accountid"
          :selected="props.selectedPayment != null && props.selectedPayment.billingaccountid == card.accountid"
          :billingAccount="card"
          @setBillingAccount="billingAccountSet"
          />
      </div>

      <GiftCardForm 
        @setGiftCard="giftCardSet"
        :canSaveCard="user"
        v-if="gc_type == 'new'"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
  import {
    reactive, computed, onMounted, ref, watch
  } from 'vue';
  import type {PropType} from 'vue';

  import type BillingAccount from '../../classes/BillingAccount';
  import type BasketBillingMethod from '../../classes/BasketBillingMethod';
  import type CreditCard from '../../classes/CreditCard';
  import type GiftCard from '../../classes/GiftCard';
  import type BillingScheme from '../../types/BillingScheme';

  import TwCheckbox from '../../components/TwCheckbox.vue';
  import BillingAccountCard from './_BillingAccountCard.vue';
  import CreditCardForm from './_CreditCardForm.vue';
  import GiftCardForm from './_GiftCardForm.vue';

  const props = defineProps({
    billingSchemes: {
      type: (Object as PropType<BillingScheme[]>),
      default: [],
    },
    billingAccounts: {
      type: (Object as PropType<BillingAccount[]>),
      default: [],
    },
    selectedPayment: {
      type: (Object as PropType<BasketBillingMethod> | null),
      required: false,
      default: null,
    },
    user: {
      type: Boolean,
      default: false,
    },
    deliverymode: {
      type: String
    }
  });

  const state = reactive({
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    contact: '',
  })

  const tender = ref('credit_card');
  const cc_type = ref('new');
  const gc_type = ref('new');

  const emit = defineEmits(['setFirstName', 'setLastName', 'setEmailAddress', 'setPhoneNumber', 'setContact', 'setBillingAccount', 'setNewCreditCard', 'setRefuel', 'resetPaymentMethod', 'setNewGiftCard'])

  onMounted(() => {
    // Default to user's saved cards list if available
    if (props.user && savedCreditCards.value && savedCreditCards.value.length > 0) {
       cc_type.value = 'saved';
    }

    if (props.user && savedGiftCards.value && savedGiftCards.value.length > 0) {
       gc_type.value = 'saved';
    }
  })

  const setCCType = ((ccType: string) => {
    cc_type.value = ccType;
    emit('resetPaymentMethod', true);
  })

  const setGCType = ((gcType: string) => {
    gc_type.value = gcType;
    emit('resetPaymentMethod', true);
  })

  const setTender = ((newTender: string) => {
    tender.value = newTender;

    if (newTender == 'refuel') {
      emit('setRefuel', true);
    } else {
      emit('resetPaymentMethod', true);
    }
  })

  const billingAccountSet = ((billingAccount: BillingAccount) => {
    emit('setBillingAccount', billingAccount);
  })

  const creditCardSet = ((creditCard: CreditCard) => {
    emit('setNewCreditCard', creditCard);
  })

  const giftCardSet = ((giftCard: GiftCard) => {
    emit('setNewGiftCard', giftCard);
  })

  const savedCreditCards = computed(() => {
    if (props.billingAccounts) {
      return props.billingAccounts.filter((acct) => {
        return acct.accounttype == 'creditcard'
      })
    } else {
      return [];
    }
  })

  const savedGiftCards = computed(() => {
    if (props.billingAccounts) {
      return props.billingAccounts.filter((acct) => {
        return acct.accounttype.toLowerCase().indexOf('gift card') > -1
      })
    } else {
      return [];
    }
  })

  const supportsCreditCard = computed(() => {
    let isSupported = false;

    if (props.billingSchemes != null) {
      isSupported = (props.billingSchemes.find((bs: BillingScheme) => {
        return bs.type == "creditcard";
      }) != null);
    }

    return isSupported; 
  })

  const supportsGiftCard = computed(() => {
    let isSupported = false;

    if (props.billingSchemes != null) {
      isSupported = (props.billingSchemes.find((bs: BillingScheme) => {
        return bs.type == "giftcard";
      }) != null);
    }

    return isSupported;
  })

  const supportsRefuel = computed(() => {
    let isSupported = false;

    if (props.billingSchemes != null) {
      isSupported = (props.billingSchemes.find((bs: BillingScheme) => {
        return bs.type == "refuel";
      }) != null);
    }

    return isSupported;
  })
</script>